import LegalLayout, { Section, P, List } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="June 12, 2026">
      <Section heading="Overview">
        <P>
          This Privacy Policy explains how DOO MADE Inc. (&ldquo;DOO MADE,&rdquo;
          &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses,
          and protects information when you visit our website at doomade.com (the
          &ldquo;Site&rdquo;) or contact us about our services. We keep data
          collection to what we actually need, and we do not sell your personal
          information.
        </P>
      </Section>

      <Section heading="Information We Collect">
        <P>We collect the following categories of information:</P>
        <List
          items={[
            <>
              <strong className="text-chalk">Information you give us.</strong> When
              you book a call or contact us, you may provide your name, email
              address, company, and any details you share about your project or
              business needs.
            </>,
            <>
              <strong className="text-chalk">Usage data.</strong> Like most
              websites, our hosting and infrastructure providers automatically
              log technical information such as your IP address, browser type,
              device, referring pages, and the dates and times of your visits.
            </>,
            <>
              <strong className="text-chalk">Cookies and similar technologies.</strong>{" "}
              We and our providers may use cookies or local storage to keep the
              Site working correctly and to understand aggregate usage. You can
              control cookies through your browser settings.
            </>,
          ]}
        />
      </Section>

      <Section heading="How We Use Your Information">
        <P>We use the information we collect to:</P>
        <List
          items={[
            "Respond to your inquiries and schedule or conduct calls.",
            "Provide, operate, and improve the Site and our services.",
            "Understand how the Site is used so we can make it better.",
            "Protect the security and integrity of the Site.",
            "Comply with our legal obligations.",
          ]}
        />
      </Section>

      <Section heading="Third-Party Services">
        <P>
          We rely on trusted third-party services to run the Site and our
          business. When you use a feature powered by one of these services, your
          information is handled under that provider&rsquo;s own privacy policy:
        </P>
        <List
          items={[
            <>
              <strong className="text-chalk">Calendly</strong> &mdash; to schedule
              calls. Information you enter when booking is processed by Calendly.
            </>,
            <>
              <strong className="text-chalk">Substack</strong> &mdash; our
              newsletter is hosted on Substack; subscribing is governed by
              Substack&rsquo;s privacy policy.
            </>,
            <>
              <strong className="text-chalk">YouTube / Google</strong> &mdash; video
              content and embeds are served by YouTube and may set cookies.
            </>,
            <>
              <strong className="text-chalk">Discord</strong> &mdash; our community
              links lead to Discord, which has its own privacy practices.
            </>,
            <>
              <strong className="text-chalk">Supabase and Vercel</strong> &mdash; we
              use these for backend data and hosting; they process technical and
              log data on our behalf.
            </>,
          ]}
        />
        <P>
          We do not control, and are not responsible for, the privacy practices
          of these third parties. We encourage you to review their policies.
        </P>
      </Section>

      <Section heading="How We Share Information">
        <P>
          We do not sell or rent your personal information. We share it only with
          the service providers described above, where required by law, or in
          connection with a business transfer (such as a merger or acquisition).
        </P>
      </Section>

      <Section heading="Data Retention">
        <P>
          We keep personal information only for as long as needed to fulfill the
          purposes described in this policy, to maintain our business records, or
          as required by law, after which we delete or anonymize it.
        </P>
      </Section>

      <Section heading="Your Rights">
        <P>
          Depending on where you live, you may have the right to access, correct,
          or delete the personal information we hold about you, or to withdraw
          consent. To make a request, contact us using the details below. As a
          federally incorporated Canadian business, we handle personal information
          in accordance with applicable law, including Canada&rsquo;s Personal
          Information Protection and Electronic Documents Act (PIPEDA) and
          applicable provincial privacy legislation.
        </P>
      </Section>

      <Section heading="Children's Privacy">
        <P>
          The Site is not directed to children under the age of 13, and we do not
          knowingly collect personal information from them.
        </P>
      </Section>

      <Section heading="Changes to This Policy">
        <P>
          We may update this Privacy Policy from time to time. When we do, we will
          revise the &ldquo;Last updated&rdquo; date at the top of this page.
          Continued use of the Site after changes take effect constitutes
          acceptance of the revised policy.
        </P>
      </Section>

      <Section heading="Contact Us">
        <P>
          If you have questions about this Privacy Policy or how we handle your
          information, contact DOO MADE Inc. at{" "}
          <a
            href="mailto:daniel.jindoo@doomade.com"
            className="text-chalk border-b border-acid/40 hover:text-acid-soft hover:border-acid transition-colors duration-200"
          >
            daniel.jindoo@doomade.com
          </a>
          .
        </P>
      </Section>
    </LegalLayout>
  );
}
